<template>
  <!-- O scroll fica no contentor em bloco: numa coluna flex com altura fixa a
       tabela (overflow: hidden) encolhia para caber e ficava cortada. -->
  <div class="h-full w-full max-w-full rounded-lg overflow-y-auto scrollbar-thin scrollbar-tema">
    <div class="w-full max-w-full flex flex-col gap-3 pb-2">
      <!-- Filtro por estado -->
      <div class="flex flex-wrap items-center gap-2 text-[var(--text)]">
        <button
          v-for="opcao in opcoesEstado"
          :key="opcao.valor"
          class="px-3 py-1 rounded-md text-sm border transition-colors"
          :class="
            estado === opcao.valor
              ? 'bg-[#ff9b05] border-[#ff9b05] text-white'
              : 'border-[#ff9b05]/50 hover:border-[#ff9b05]'
          "
          @click="estado = opcao.valor"
        >
          {{ opcao.label }} ({{ contagem(opcao.valor) }})
        </button>
      </div>

      <!-- Totais dos contratos filtrados -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-2 text-[var(--text)]">
        <div
          v-for="total in totais"
          :key="total.label"
          class="rounded-lg p-3 bg-black/20 border border-[var(--glass-border)] flex flex-col"
        >
          <span class="text-xs opacity-70">{{ total.label }}</span>
          <span class="text-lg font-semibold">{{ formatarValor(total.valor) }}</span>
        </div>
      </div>

      <div v-if="loading" class="text-center py-10 text-[var(--text)]">
        <i class="fa-solid fa-spinner fa-spin text-2xl"></i>
        <p class="mt-2">A carregar contratos...</p>
      </div>
      <GenericDataTable
        class="border border-[var(--glass-border)]"
        v-else
        :key="`contratos-${estado}`"
        :columns="colunas"
        :tableData="contratosFiltrados"
        :useServerSide="false"
        :show_export="true"
        :show_close_btn="false"
        export_filename="contratos"
        table_title="Contratos"
        :rowClickNavigates="true"
        @row-click="abrirDetalhe"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { toast } from "vue3-toastify";
import GenericDataTable from "@/components/dataTable/GenericDataTable2.vue";

// Todos os contratos (em vigor, expirados e futuros), uma linha por contrato
// agrupado, com o faturado nas instalações/famílias do contrato durante o seu
// período. Dados e regras: ContratoResumoView (backend/api/Views/contratoViews.py).
const ESTADO_CLASSES = {
  "Em vigor": "bg-green-600 text-white",
  Expirado: "bg-red-600 text-white",
  Futuro: "bg-blue-600 text-white",
};

export default {
  name: "appContratos",
  components: {
    GenericDataTable,
  },
  data() {
    return {
      baseURl: process.env.VUE_APP_URL_BASE,
      contratos: [],
      loading: false,
      estado: "todos",
    };
  },
  computed: {
    opcoesEstado() {
      const opcoes = [
        { valor: "todos", label: "Todos" },
        { valor: "Em vigor", label: "Em vigor" },
        { valor: "Expirado", label: "Expirados" },
      ];
      if (this.contratos.some((c) => c.estado === "Futuro")) {
        opcoes.push({ valor: "Futuro", label: "Futuros" });
      }
      return opcoes;
    },
    contratosFiltrados() {
      if (this.estado === "todos") return this.contratos;
      return this.contratos.filter((c) => c.estado === this.estado);
    },
    totais() {
      const soma = (campo) =>
        this.contratosFiltrados.reduce((t, c) => t + (parseFloat(c[campo]) || 0), 0);
      return [
        { label: "Valor global", valor: soma("valor_global") },
        { label: "Faturas preventivas", valor: soma("faturas_preventivas") },
        { label: "Faturas corretivas", valor: soma("faturas_corretivas") },
        { label: "Total faturas", valor: soma("total_faturas") },
      ];
    },
    colunas() {
      // Com dados locais a tabela ignora `computed`; formata-se no `render`,
      // que mantém a ordenação pelo valor bruto.
      const valor = (key, label) => ({
        key,
        label,
        maxWidth: 110,
        sortable: true,
        render: (data, type) => (type === "display" ? this.formatarValor(data) : data),
      });
      return [
        { key: "cliente_nome", label: "Cliente", maxWidth: 160, sortable: true },
        { key: "subcliente_nome", label: "SubCliente", maxWidth: 160, sortable: true },
        { key: "nome", label: "Contrato", maxWidth: 180, sortable: true },
        { key: "tipo_contrato_nome", label: "Tipo", maxWidth: 100, sortable: true },
        { key: "familia_nome", label: "Famílias", maxWidth: 130, sortable: true },
        { key: "data_inicio", label: "Data Início", maxWidth: 100, sortable: true, type: "date" },
        { key: "data_validade", label: "Data Fim", maxWidth: 100, sortable: true, type: "date" },
        {
          key: "estado",
          label: "Estado",
          maxWidth: 90,
          sortable: true,
          render: (data, type) =>
            type === "display"
              ? `<span class="px-2 py-0.5 rounded text-xs ${ESTADO_CLASSES[data] || ""}">${data}</span>`
              : data,
        },
        valor("valor_global", "Valor Global"),
        valor("faturas_preventivas", "Faturas Preventivas"),
        valor("faturas_corretivas", "Faturas Corretivas"),
        valor("total_faturas", "Total Faturas"),
      ];
    },
  },
  async mounted() {
    await this.carregar();
  },
  methods: {
    async carregar() {
      this.loading = true;
      try {
        const response = await axios.get(`${this.baseURl}/api/v1/contratos/resumo/`);
        this.contratos = response.data?.data || [];
      } catch (error) {
        console.error("Erro ao carregar contratos:", error);
        toast.error("Erro ao carregar contratos");
        this.contratos = [];
      } finally {
        this.loading = false;
      }
    },
    contagem(estado) {
      if (estado === "todos") return this.contratos.length;
      return this.contratos.filter((c) => c.estado === estado).length;
    },
    formatarValor(valor) {
      if (valor === null || valor === undefined || valor === "") return "";
      return new Intl.NumberFormat("pt-PT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(valor));
    },
    abrirDetalhe(linha) {
      this.$router.push({ name: "contrato-detalhe", params: { id: linha.id } });
    },
  },
};
</script>
